// https://headlessui.dev/react/listbox
import { Listbox, Transition } from '@headlessui/react'
import React, { Fragment } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { classNames } from '@g4g/utils/src/react/class-names'

export interface SelectItem {
  id: number
  label: React.ReactNode
  disabled: boolean
}

interface IHeadlessSelectProps {
  disabled?: boolean
  selectedItem?: SelectItem
  items: SelectItem[]
  setSelectedItem: (selectedId: SelectItem) => void
  parentClassName?: string
  buttonClassName?: string
  dropdownClassNames?: {
    /** Class for not selected/not active. */
    optionDefault?: string
    /** Class for currently hovered (active) item. */
    optionActive?: string
  }
  dropdownBackgroundClassName?: string
  placeholder?: string
}

const HeadlessSelect: React.FC<IHeadlessSelectProps> = ({
  selectedItem,
  items,
  setSelectedItem,
  parentClassName = '',
  buttonClassName = '',
  dropdownClassNames,
  dropdownBackgroundClassName = '',
  placeholder,
  disabled,
}) => {
  return (
    <Listbox disabled={disabled} value={selectedItem} onChange={setSelectedItem}>
      <div
        data-cy="headless-select"
        className={classNames(
          'flex relative flex-col flex-grow text-white',
          parentClassName,
          disabled && 'opacity-50'
        )}
      >
        <Listbox.Button
          className={classNames(
            'relative w-full transition-all my-auto',
            'px-2 py-1.5 text-left rounded-lg',
            'text-xs sm:text-sm outline-none',
            disabled
              ? 'cursor-default'
              : 'cursor-pointer ring-1 hover:ring-offset-1 focus-visible:ring-2 focus-visible:ring-offset-2',
            buttonClassName
          )}
        >
          <span data-cy="ui-select-value" className="block mr-5 font-poppins">
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          <span
            className={classNames(
              'absolute inset-y-0 right-0 flex',
              'items-center py-0 pr-2 pointer-events-none'
            )}
          >
            <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>
        <Transition
          className="z-40"
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Listbox.Options
            className={classNames(
              'border border-white/30 overflow-auto absolute',
              'mt-1 min-w-full max-h-60 text-base',
              'rounded-md ring-1 ring-black ring-opacity-5',
              'shadow-lg noScrollbar focus:outline-none sm:text-sm',
              dropdownBackgroundClassName
            )}
          >
            {items.map((item) => (
              <Listbox.Option as={Fragment} key={item.id} value={item} disabled={item.disabled}>
                {({ active, selected }) => (
                  <li
                    className={classNames(
                      'cursor-pointer select-none relative py-2 px-2 sm:pl-4 sm:pr-10 text-xs sm:text-sm',
                      active &&
                        `ring-1 ring-white/30 ${dropdownClassNames?.optionActive ?? 'text-white'}`,
                      !active &&
                        !selected &&
                        (dropdownClassNames?.optionDefault ?? 'text-secondaryLight'),
                      selected && 'text-white'
                    )}
                  >
                    <span
                      className={classNames(
                        'block whitespace-nowrap',
                        selected ? 'font-medium' : 'font-normal'
                      )}
                    >
                      {item.label}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                      </span>
                    )}
                  </li>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

export default HeadlessSelect
